import { computed } from '@vue/composition-api';
import { useStore } from '@nuxtjs/composition-api';
import { endOfISOWeek, format, isSameDay, parse, startOfISOWeek } from 'date-fns';
import { TrainingSummary } from 'mottiv-web-components';

import { useUser } from '~/data/account/use-user';
import { DateFormat } from '~/helpers/date';
import { State } from '~/data/types/store';
import { CalendarDay } from '~/data/types';
import { useFeatureFlag } from '~/data/account/use-feature-flag';

export const useTrainingSummary = () => {
  const store = useStore<State>();
  const { currentDate } = useUser();
  const { hasAccessToDashboardV2 } = useFeatureFlag();
  const isLoadingSummaries = computed(() => store.state.trainingSummary.isLoadingSummaries.length);

  const isLoadingSummary = (fromDate: string, toDate: string): boolean => {
    const key = `${fromDate}-${toDate}`;
    return isLoadingSummaryForKey(key);
  };

  const isLoadingSummaryForCurrentWeek = (): boolean => {
    const fromDate = format(startOfISOWeek(currentDate.value), DateFormat.Date);
    const toDate = format(currentDate.value, DateFormat.Date);
    const key = `${fromDate}-${toDate}`;
    return isLoadingSummaryForKey(key);
  };

  const isLoadingSummaryForKey = (key: string): boolean => {
    return store.state.trainingSummary.isLoadingSummaries.includes(key);
  };

  const getSummaries = async(): Promise<boolean> => {
    try {
      await store.dispatch('trainingSummary/GET_TRAINING_SUMMARIES');
      return true;
    } catch {
      return false;
    }
  };

  const getSummary = async(fromDate: string, toDate: string, forceUpdate: boolean = false): Promise<TrainingSummary | null> => {
    const key = `${fromDate}-${toDate}`;

    if (isLoadingSummaryForKey(key)) {
      return null;
    }

    if (store.state.trainingSummary.summaries[key] && !forceUpdate) {
      return store.state.trainingSummary.summaries[key];
    }

    try {
      await store.dispatch('trainingSummary/GET_TRAINING_SUMMARY', {
        fromDate,
        toDate,
        useDateRange: hasAccessToDashboardV2.value,
      });
      return store.state.trainingSummary.summaries[key] ?? null;
    } catch {
      return null;
    }
  };

  const getSummaryForCurrentWeek = async(): Promise<TrainingSummary | null> => {
    const fromDate = format(startOfISOWeek(currentDate.value), DateFormat.Date);
    const toDate = format(currentDate.value, DateFormat.Date);
    const key = `${fromDate}-${toDate}`;

    if (isLoadingSummaryForKey(key)) {
      return null;
    }

    if (store.state.trainingSummary.summaries[key]) {
      return store.state.trainingSummary.summaries[key];
    }

    try {
      await store.dispatch('trainingSummary/GET_TRAINING_SUMMARY', {
        fromDate,
        toDate,
        useDateRange: hasAccessToDashboardV2.value,
      });
      return store.state.trainingSummary.summaries[key] ?? null;
    } catch {
      return null;
    }
  };

  const getSummaryActivityTypesFromCalendar = (days: CalendarDay[]): string[] => {
    let hasSwim = false;
    let hasBike = false;
    let hasRun = false;
    let hasOther = false;

    days.forEach((day) => {
      day.activities.forEach((activity) => {
        const activityType = (activity.type ?? '').toLowerCase();
        if (activityType.includes('swim')) {
          hasSwim = true;
        } else if (activityType.includes('bike')) {
          hasBike = true;
        } else if (activityType.includes('run')) {
          hasRun = true;
        } else {
          hasOther = true;
        }
      });
    });

    const activities: string[] = [];

    if (hasSwim) {
      activities.push('swim');
    }
    if (hasBike) {
      activities.push('bike');
    }
    if (hasRun) {
      activities.push('run');
    }
    if (hasOther) {
      activities.push('other');
    }

    return activities;
  };

  const updateWeekSummary = (date: string) => {
    const startOfWeek = startOfISOWeek(parse(date, DateFormat.Date, currentDate.value));
    let endOfWeek: Date;

    // If this is the current week, then make the end of week today
    if (isSameDay(startOfISOWeek(currentDate.value), startOfWeek)) {
      endOfWeek = currentDate.value;
    } else {
      endOfWeek = endOfISOWeek(startOfWeek);
    }

    getSummary(format(startOfWeek, DateFormat.Date), format(endOfWeek, DateFormat.Date), true);
  };

  return {
    getSummaries,
    getSummary,
    getSummaryActivityTypesFromCalendar,
    getSummaryForCurrentWeek,
    isLoadingSummaries,
    isLoadingSummary,
    isLoadingSummaryForCurrentWeek,
    isLoadingSummaryForKey,
    updateWeekSummary,
  };
};
